import React, { useState } from "react";
import {
  Typography,
  IconButton,
  Box,
  Card,
  CardContent,
  Button,
  Avatar,
  Tooltip,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import LogoutIcon from "@mui/icons-material/Logout";
import { useTheme } from "@mui/material/styles";
import axios from "axios";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import Preloader from "./components/Preloader";
import { handleLogout } from "./utils/utils";
import { getOrganizationSettings } from "./utils/storage";
import axiosPrivate from "./api/axiosPrivate";

export default function Settings({ setSidebarOpen }) {
  const [form, setForm] = useState({
    current_password: "",
    new_password: "",
    confirm_password: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [show, setShow] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  const [pinForm, setPinForm] = useState({
    current_pin: "",
    new_pin: "",
    confirm_pin: "",
  });
  const [pinLoading, setPinLoading] = useState(false);
  const [pinError, setPinError] = useState("");
  const [pinSuccess, setPinSuccess] = useState("");
  const [showPin, setShowPin] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  let user = null;
  try {
    user = JSON.parse(localStorage.getItem("user"));
  } catch { }

  if (loading || pinLoading)
    return <Preloader fullScreen={true} message="Updating your settings..." />;

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleShow = (field) => setShow((s) => ({ ...s, [field]: !s[field] }));

  const handlePinChange = (e) => {
    const value = e.target.value.replace(/\D/g, "");
    setPinForm({ ...pinForm, [e.target.name]: value });
  };

  const handleShowPin = (field) =>
    setShowPin((s) => ({ ...s, [field]: !s[field] }));

  const handlePinSubmit = async (e) => {
    e.preventDefault();
    setPinError("");
    setPinSuccess("");
    if (pinForm.new_pin !== pinForm.confirm_pin) {
      setPinError("New PINs do not match");
      return;
    }
    setPinLoading(true);
    try {
      const response = await axiosPrivate.post("change-transaction-pin", {
        current_pin: pinForm.current_pin,
        new_pin: pinForm.new_pin,
        new_pin_confirmation: pinForm.confirm_pin,
      });

      setPinSuccess(
        response?.data?.message || "Transaction PIN changed successfully"
      );
      setPinForm({ current_pin: "", new_pin: "", confirm_pin: "" });
    } catch (err) {
      setPinError(
        err.response?.data?.message ||
        err.response?.data?.error ||
        "Failed to change transaction PIN"
      );
    } finally {
      setPinLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    if (form.new_password !== form.confirm_password) {
      setError("New passwords do not match");
      return;
    }
    setLoading(true);
    try {

      const response = await axiosPrivate.post('change-password', {
        current_password: form.current_password,
        new_password: form.new_password,
        new_password_confirmation: form.new_password,
      });

      setSuccess("Password changed successfully!");
      setForm({ current_password: "", new_password: "", confirm_password: "" });
    } catch (err) {
      setError(err.response?.data?.error || "Failed to change password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-4 sm:px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 sm:gap-4">
            <button
              onClick={() => setSidebarOpen && setSidebarOpen(true)}
              className="block xl:hidden text-gray-600 hover:text-gray-900 p-1"
            >
              <MenuIcon />
            </button>
            <div className="min-w-0 flex-1">
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 truncate">
                Settings
              </h1>
              <p className="text-sm text-gray-600 truncate">
                Manage your account settings
              </p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="text-gray-600 hover:text-red-600 transition p-1 flex-shrink-0"
          >
            <LogoutIcon />
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="px-4 sm:px-6 py-6 sm:py-8">
        <div className="max-w-2xl mx-auto">
          <Card className="bg-white border border-gray-200 shadow-sm">
            <CardContent className="p-6">
              <Typography variant="h6" className="text-gray-900 mb-4">
                Change Password
              </Typography>
              <form onSubmit={handleSubmit}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Current Password
                  </label>
                  <div className="relative">
                    <input
                      type={show.current ? "text" : "password"}
                      name="current_password"
                      value={form.current_password}
                      onChange={handleChange}
                      required
                      className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <IconButton
                      onClick={() => handleShow("current")}
                      aria-label={
                        show.current ? "Hide password" : "Show password"
                      }
                      size="small"
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 z-10"
                      sx={{
                        position: "absolute",
                        right: 8,
                        top: "50%",
                        transform: "translateY(-50%)",
                      }}
                    >
                      {show.current ? (
                        <VisibilityOffIcon />
                      ) : (
                        <VisibilityIcon />
                      )}
                    </IconButton>
                  </div>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    New Password
                  </label>
                  <div className="relative">
                    <input
                      type={show.new ? "text" : "password"}
                      name="new_password"
                      value={form.new_password}
                      onChange={handleChange}
                      required
                      className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <IconButton
                      onClick={() => handleShow("new")}
                      aria-label={show.new ? "Hide password" : "Show password"}
                      size="small"
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 z-10"
                      sx={{
                        position: "absolute",
                        right: 8,
                        top: "50%",
                        transform: "translateY(-50%)",
                      }}
                    >
                      {show.new ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </div>
                </div>
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Confirm New Password
                  </label>
                  <div className="relative">
                    <input
                      type={show.confirm ? "text" : "password"}
                      name="confirm_password"
                      value={form.confirm_password}
                      onChange={handleChange}
                      required
                      className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <IconButton
                      onClick={() => handleShow("confirm")}
                      aria-label={
                        show.confirm ? "Hide password" : "Show password"
                      }
                      size="small"
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 z-10"
                      sx={{
                        position: "absolute",
                        right: 8,
                        top: "50%",
                        transform: "translateY(-50%)",
                      }}
                    >
                      {show.confirm ? (
                        <VisibilityOffIcon />
                      ) : (
                        <VisibilityIcon />
                      )}
                    </IconButton>
                  </div>
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  variant="contained"
                  color="primary"
                  fullWidth
                  className="mb-4 bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 rounded-md"
                >
                  {loading ? "Changing..." : "Change Password"}
                </Button>
                {error && (
                  <Typography
                    color="error"
                    className="text-center text-red-600"
                  >
                    {error}
                  </Typography>
                )}
                {success && (
                  <Snackbar
                    open={success != ""}
                    autoHideDuration={6000}
                    onClose={() => setSuccess("")}
                  >
                    <Alert
                      onClose={() => setSuccess("")}
                      severity="success"
                      sx={{ width: "100%" }}
                      elevation={6}
                      variant="filled"
                    >
                      {success}
                    </Alert>
                  </Snackbar>
                )}
              </form>

              <div className="mt-8 pt-8 border-t border-gray-200">
                <Typography variant="h6" className="text-gray-900 mb-4">
                  Change Transaction PIN
                </Typography>
                <form onSubmit={handlePinSubmit}>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Current PIN
                    </label>
                    <div className="relative">
                      <input
                        type={showPin.current ? "text" : "password"}
                        name="current_pin"
                        value={pinForm.current_pin}
                        onChange={handlePinChange}
                        required
                        inputMode="numeric"
                        maxLength={4}
                        className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      <IconButton
                        onClick={() => handleShowPin("current")}
                        aria-label={showPin.current ? "Hide PIN" : "Show PIN"}
                        size="small"
                        className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 z-10"
                        sx={{
                          position: "absolute",
                          right: 8,
                          top: "50%",
                          transform: "translateY(-50%)",
                        }}
                      >
                        {showPin.current ? (
                          <VisibilityOffIcon />
                        ) : (
                          <VisibilityIcon />
                        )}
                      </IconButton>
                    </div>
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      New PIN
                    </label>
                    <div className="relative">
                      <input
                        type={showPin.new ? "text" : "password"}
                        name="new_pin"
                        value={pinForm.new_pin}
                        onChange={handlePinChange}
                        required
                        inputMode="numeric"
                        maxLength={4}
                        className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      <IconButton
                        onClick={() => handleShowPin("new")}
                        aria-label={showPin.new ? "Hide PIN" : "Show PIN"}
                        size="small"
                        className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 z-10"
                        sx={{
                          position: "absolute",
                          right: 8,
                          top: "50%",
                          transform: "translateY(-50%)",
                        }}
                      >
                        {showPin.new ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </div>
                  </div>
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Confirm New PIN
                    </label>
                    <div className="relative">
                      <input
                        type={showPin.confirm ? "text" : "password"}
                        name="confirm_pin"
                        value={pinForm.confirm_pin}
                        onChange={handlePinChange}
                        required
                        inputMode="numeric"
                        maxLength={4}
                        className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      <IconButton
                        onClick={() => handleShowPin("confirm")}
                        aria-label={showPin.confirm ? "Hide PIN" : "Show PIN"}
                        size="small"
                        className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 z-10"
                        sx={{
                          position: "absolute",
                          right: 8,
                          top: "50%",
                          transform: "translateY(-50%)",
                        }}
                      >
                        {showPin.confirm ? (
                          <VisibilityOffIcon />
                        ) : (
                          <VisibilityIcon />
                        )}
                      </IconButton>
                    </div>
                  </div>
                  <Button
                    type="submit"
                    disabled={pinLoading}
                    variant="contained"
                    color="primary"
                    fullWidth
                    className="mb-4 bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 rounded-md"
                  >
                    {pinLoading ? "Changing..." : "Change PIN"}
                  </Button>
                  <div style={{ marginTop: '20px' }} />
                  {pinError && (
                    <Typography
                      color="error"
                      className="text-center text-red-600"
                    >
                      {pinError}
                    </Typography>
                  )}
                  {pinSuccess && (
                    <Snackbar
                      open={pinSuccess != ""}
                      autoHideDuration={6000}
                      onClose={() => setPinSuccess("")}
                    >
                      <Alert
                        onClose={() => setPinSuccess("")}
                        severity="success"
                        sx={{ width: "100%" }}
                        elevation={6}
                        variant="filled"
                      >
                        {pinSuccess}
                      </Alert>
                    </Snackbar>
                  )}
                </form>
              </div>

              <div className="mt-8 space-y-6">
                <div>
                  <Typography variant="h6" className="text-gray-900 mb-2">
                    Security
                  </Typography>
                  <Typography className="text-gray-600">
                    Coming soon: 2FA, etc.
                  </Typography>
                </div>
                <div>
                  <Typography variant="h6" className="text-gray-900 mb-2">
                    Notifications
                  </Typography>
                  <Typography className="text-gray-600">
                    Coming soon: Email/SMS preferences.
                  </Typography>
                </div>
                <div>
                  <Typography variant="h6" className="text-gray-900 mb-2">
                    Preferences
                  </Typography>
                  <Typography className="text-gray-600">
                    Coming soon: Theme, language, etc.
                  </Typography>
                </div>
              </div>

              <Button
                onClick={handleLogout}
                variant="contained"
                color="error"
                fullWidth
                className="mt-8 bg-red-600 hover:bg-red-700 text-white font-medium py-3 rounded-md"
              >
                Logout
              </Button>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
